import { isCurrentStage } from '@/core/utils/environment'

import { DEVTOOLS_QUERY_PARAM, DEVTOOLS_SESSION_ACTIVE_STORAGE_KEY } from './constants'
import { getDevToolsToken } from './cookies'

function firstQueryString(value: string | string[] | undefined): string | undefined {
  if (value === undefined) return undefined
  const raw = Array.isArray(value) ? value[0] : value
  if (raw === undefined || raw === '') return undefined
  return raw
}

export function isDevToolsQueryValueEnabled(
  value: string | string[] | undefined
): boolean {
  return firstQueryString(value)?.toLowerCase() === 'true'
}

export function isDevToolsQueryValueExplicitlyDisabled(
  value: string | string[] | undefined
): boolean {
  return firstQueryString(value)?.toLowerCase() === 'false'
}

function devToolsQueryValueFromSearch(search: string): string | undefined {
  try {
    const normalized = search.startsWith('?') ? search.slice(1) : search
    const params = new URLSearchParams(normalized)
    if (!params.has(DEVTOOLS_QUERY_PARAM)) {
      return undefined
    }
    return params.get(DEVTOOLS_QUERY_PARAM) ?? undefined
  } catch {
    return undefined
  }
}

export function isDevToolsQueryParamEnabledInSearch(search: string): boolean {
  return isDevToolsQueryValueEnabled(devToolsQueryValueFromSearch(search))
}

export function isDevToolsQueryParamExplicitlyDisabledInSearch(
  search: string
): boolean {
  return isDevToolsQueryValueExplicitlyDisabled(devToolsQueryValueFromSearch(search))
}

export function isDevToolsQueryParamEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return isDevToolsQueryParamEnabledInSearch(window.location.search)
}

export function isDevToolsQueryParamExplicitlyDisabled(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return isDevToolsQueryParamExplicitlyDisabledInSearch(window.location.search)
}

function runWithSessionStorage<T>(fallback: T, op: (storage: Storage) => T): T {
  if (typeof window === 'undefined') {
    return fallback
  }
  try {
    return op(sessionStorage)
  } catch {
    return fallback
  }
}

export function readDevToolsSessionActive(): boolean {
  return runWithSessionStorage(
    false,
    (storage) => storage.getItem(DEVTOOLS_SESSION_ACTIVE_STORAGE_KEY) === '1'
  )
}

export function writeDevToolsSessionActive(): void {
  runWithSessionStorage(undefined, (storage) => {
    storage.setItem(DEVTOOLS_SESSION_ACTIVE_STORAGE_KEY, '1')
  })
}

export function clearDevToolsSessionActive(): void {
  runWithSessionStorage(undefined, (storage) => {
    storage.removeItem(DEVTOOLS_SESSION_ACTIVE_STORAGE_KEY)
  })
}

/**
 * Checks if DevTools token entry should be shown
 * Shows token input if query param is enabled and no token exists in cookie
 */
export function shouldShowTokenInput(): boolean {
  if (typeof window === 'undefined') return false
  if (!isCurrentStage('prod')) return false
  return isDevToolsQueryParamEnabled() && !getDevToolsToken()
}
